import { ApolloClient, NormalizedCacheObject, gql } from '@apollo/client';
import { IncomingHttpHeaders } from 'http';

import {
  CreateCartMutation,
  CreateCartMutationVariables,
} from '../../ts/@types/generated/graphql/cart-order-service/graphql';
import { ClientNames } from '../../utils/apolloClient';
import { CART_FIELDS } from './cartsv2/fragments';

export const CREATE_CART = gql`
  mutation createCart(
    $funnelData: CreateFunnelDataInput!
    $cartData: CreateCartInput!
  ) {
    createCart(funnelData: $funnelData, cartData: $cartData) {
      uuid
      funnelData
    }
  }
`;

export const CREATE_AND_RETURN_CART = gql`
  mutation createAndReturnCart(
    $funnelData: CreateFunnelDataInput!
    $cartData: CreateCartInput!
  ) {
    createCart(funnelData: $funnelData, cartData: $cartData) {
      ...allCartFields
    }
  }
  ${CART_FIELDS}
`;
// @todo we should really use fragments to ensure we're querying for same fields, but we can fix that as tech-debt.
export const UPDATE_CART = gql`
  mutation updateCart(
    $funnelData: UpdateFunnelDataInput
    $cartData: UpdateCartInput
    $uuid: UUID!
  ) {
    updateCart: updateCartV2(
      funnelData: $funnelData
      cartData: $cartData
      uuid: $uuid
    ) {
      ...allCartFields
      ... on CartError {
        validation {
          status {
            message
          }
        }
      }
    }
  }
  ${CART_FIELDS}
`;

export const ADD_VARIANT_TO_CART_DELIVERY = gql`
  mutation addVariantToCartDelivery(
    $cartUUID: UUID!
    $cartDeliveryId: Int!
    $variantInput: AddVariantToCartDeliveryInput!
  ) {
    addVariantToCartDelivery: addVariantToCartDeliveryV2(
      cartUUID: $cartUUID
      cartDeliveryId: $cartDeliveryId
      variantInput: $variantInput
    ) {
      ... on CartDelivery {
        cart {
          uuid
          userId
          orderOn
          deliveryDates
          cartDeliveries {
            id
            createdAt
            updatedAt
            locationId
            status
            deliveryType
            estimatedTransitTime
            estimatedDeliveryDate
            deliveryDate
            lineItems {
              id
              createdAt
              updatedAt
              variantId
              variant
              price
              quantity
            }
          }
          funnelData
          cartLedger {
            id
            createdAt
            updatedAt
            subtotal
            discountTotal
            deliveryTotal
            tax
            grandTotal
            creditTotal
            precreditTotal
          }
          status
          cartError {
            validation {
              quantity {
                message
                deliveryId
              }
            }
          }
        }
      }
      ... on CartError {
        validation {
          status {
            message
          }
          quantity {
            message
            deliveryId
          }
          soldOutItem {
            message
          }
        }
      }
    }
  }
`;

export const REMOVE_VARIANT_FROM_CART_DELIVERY = gql`
  mutation removeVariantFromCartDelivery(
    $cartUUID: UUID!
    $cartDeliveryId: Int!
    $variantInput: RemoveVariantInput!
  ) {
    removeVariantFromCartDelivery: removeVariantFromCartDeliveryV2(
      cartUUID: $cartUUID
      cartDeliveryId: $cartDeliveryId
      variantInput: $variantInput
    ) {
      ... on CartDelivery {
        cart {
          uuid
          userId
          orderOn
          deliveryDates
          cartDeliveries {
            id
            createdAt
            updatedAt
            locationId
            status
            deliveryType
            estimatedTransitTime
            estimatedDeliveryDate
            deliveryDate
            lineItems {
              id
              createdAt
              updatedAt
              variantId
              variant
              price
              quantity
            }
          }
          funnelData
          cartLedger {
            id
            createdAt
            updatedAt
            subtotal
            creditTotal
            discountTotal
            deliveryTotal
            tax
            grandTotal
            precreditTotal
          }
          status
          cartError {
            validation {
              status {
                message
              }
              quantity {
                message
                deliveryId
              }
            }
          }
        }
      }
      ... on CartError {
        validation {
          status {
            message
          }
          quantity {
            message
            deliveryId
          }
        }
      }
    }
  }
`;

export const ADD_CODE_TO_CART = gql`
  mutation addCodeToCart($code: String!, $uuid: UUID!) {
    addCodeToCart: addCodeToCartV2(code: $code, uuid: $uuid) {
      ... on Cart {
        uuid
        userId
        orderOn
        deliveryDates
        funnelData
        cartDeliveries {
          id
          createdAt
          updatedAt
          locationId
          status
          deliveryType
          estimatedTransitTime
          estimatedDeliveryDate
          deliveryDate
          lineItems {
            id
            createdAt
            updatedAt
            variantId
            variant
            price
            quantity
          }
        }
        cartLedger {
          id
          createdAt
          updatedAt
          subtotal
          discountTotal
          deliveryTotal
          creditTotal
          tax
          grandTotal
          precreditTotal
        }
        discount {
          amount
          code
          type
        }
        status
        discountErrors
      }
      ... on CartError {
        validation {
          status {
            message
          }
        }
      }
    }
  }
`;

export const REPRICE_CART = gql`
  mutation repriceCart($uuid: UUID!) {
    repriceCart: repriceCartV2(uuid: $uuid) {
      ... on Cart {
        uuid
        userId
        orderOn
        deliveryDates
        funnelData
        status
        cartDeliveries {
          id
          createdAt
          updatedAt
          locationId
          status
          deliveryType
          estimatedTransitTime
          estimatedDeliveryDate
          deliveryDate
          lineItems {
            id
            createdAt
            updatedAt
            variantId
            variant
            price
            quantity
          }
        }
        cartLedger {
          id
          createdAt
          updatedAt
          subtotal
          discountTotal
          deliveryTotal
          creditTotal
          tax
          grandTotal
          precreditTotal
        }
        discount {
          amount
          code
          type
        }
      }
      ... on CartError {
        validation {
          status {
            message
          }
        }
      }
    }
  }
`;

/**
 * @returns Cart UUID if cart was created, else returns null. This variation returns minimum data.
 */
export async function createCart(
  client: ApolloClient<NormalizedCacheObject>,
  headers: IncomingHttpHeaders,
  variables: CreateCartMutationVariables,
  clientName = ClientNames.CART_ORDER_GRAPH,
): Promise<string | null> {
  const response = await client.mutate<
    CreateCartMutation,
    CreateCartMutationVariables
  >({
    mutation: CREATE_CART,
    variables,
    context: { headers, clientName },
  });
  return response?.data?.createCart.uuid;
}

/**
 * @returns the full Cart object
 */
export async function createAndReturnCart(
  client: ApolloClient<NormalizedCacheObject>,
  headers: IncomingHttpHeaders,
  variables: CreateCartMutationVariables,
  clientName = ClientNames.CART_ORDER_GRAPH,
): Promise<string | null> {
  const response = await client.mutate<
    CreateCartMutation,
    CreateCartMutationVariables
  >({
    mutation: CREATE_CART,
    variables,
    context: { headers, clientName },
  });
  return response?.data?.createCart.uuid;
}

/**
 * Update an existing cart delivery.
 *
 * Extend return type if you need.
 */
export const UPDATE_CART_DELIVERY = gql`
  mutation updateCartDelivery(
    $id: Int!
    $deliveryData: UpdateCartDeliveryInput!
  ) {
    updateCartDelivery: updateCartDeliveryV2(
      id: $id
      deliveryData: $deliveryData
    ) {
      ... on CartDelivery {
        id
        status
        estimatedTransitTime
        estimatedDeliveryDate
      }
      ... on CartError {
        validation {
          status {
            message
          }
          soldOutItem {
            message
          }
        }
      }
    }
  }
`;

export const EVALUATE_DRAFT_CART = gql`
  mutation EvaluateDraftCart(
    $draftCart: EvaluateDraftCartInput!
    $zip: String!
    $state: String
    $recurringSubscription: Boolean!
  ) {
    evaluateDraftCart(
      draftCart: $draftCart
      zip: $zip
      state: $state
      recurringSubscription: $recurringSubscription
    ) {
      discount {
        code
        amount
        type
      }
      deliveryDates
      cartDeliveries {
        estimatedTransitTime
        estimatedDeliveryDate
        deliveryDate
        lineItems {
          variantId
          quantity
          price
          variant
        }
      }
      cartLedger {
        subtotal
        discountTotal
        creditTotal
        deliveryTotal
        tax
        precreditTotal
        grandTotal
      }
      cartError {
        validation {
          quantity {
            message
          }
          catalog {
            message
          }
          soldOutItem {
            message
          }
        }
      }
    }
  }
`;

export const UPDATE_CART_V2 = gql`
  mutation UpdateCartV2($uuid: UUID!, $cartData: UpdateCartInput!) {
    updateCartV2(uuid: $uuid, cartData: $cartData) {
      ... on CartError {
        __typename
        validation {
          quantity {
            message
          }
          catalog {
            message
          }
          status {
            message
          }
          soldOutItem {
            message
          }
        }
      }
      ... on Cart {
        __typename
        uuid
        userId
        legacyOrderId
        status
        funnelData
        deliveryDates
        cartError {
          validation {
            quantity {
              message
            }
            status {
              message
            }
            catalog {
              message
            }
          }
        }
        cartDeliveries {
          id
          createdAt
          updatedAt
          locationId
          status
          deliveryType
          estimatedTransitTime
          estimatedDeliveryDate
          deliveryDate
          lineItems {
            id
            createdAt
            updatedAt
            variantId
            variant
            price
            quantity
          }
        }
        cartLedger {
          subtotal
          discountTotal
          creditTotal
          deliveryTotal
          tax
          precreditTotal
          grandTotal
        }
      }
    }
  }
`;
